// src/utils/employees.js — stały vs tymczasowy pracownik (addytywne flagi na employees[])
import { parseStaffEntry } from './staffEntry';

export function composeEmployeeName(firstName, lastName) {
    return [firstName, lastName]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(' ');
}

export function splitEmployeeName(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { firstName: '', lastName: '' };
    if (parts.length === 1) return { firstName: parts[0], lastName: '' };
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

export function getEmployeeName(emp) {
    if (typeof emp === 'string') return emp.trim();
    if (!emp || typeof emp !== 'object') return '';
    const direct = String(emp.name || '').trim();
    if (direct) return direct;
    return composeEmployeeName(emp.firstName, emp.lastName);
}

function asEmployeeObject(emp) {
    if (typeof emp === 'string') {
        const name = emp.trim();
        return name ? { name } : null;
    }
    if (!emp || typeof emp !== 'object') return null;
    const name = getEmployeeName(emp);
    if (!name) return null;
    return { ...emp, name };
}

export function isTemporaryEmployee(emp) {
    return !!(emp && typeof emp === 'object' && emp.isTemporary === true);
}

export function isArchivedEmployee(emp) {
    return !!(emp && typeof emp === 'object' && emp.archived === true);
}

export function isPermanentEmployee(emp) {
    if (typeof emp === 'string') return !!emp.trim();
    return !!getEmployeeName(emp) && !isTemporaryEmployee(emp) && !isArchivedEmployee(emp);
}

export function isAssignableTemporary(emp) {
    return isTemporaryEmployee(emp) && !isArchivedEmployee(emp);
}

export function listPermanentEmployees(list) {
    return (list || []).filter(isPermanentEmployee);
}

export function listTemporaryEmployees(list, { includeArchived = false } = {}) {
    return (list || []).filter((emp) =>
        includeArchived ? isTemporaryEmployee(emp) : isAssignableTemporary(emp)
    );
}

export function createTemporaryEmployee({
    firstName = '',
    lastName = '',
    phone = '',
    email = '',
    note = '',
    name = '',
} = {}) {
    const first = String(firstName || '').trim();
    const last = String(lastName || '').trim();
    const display = composeEmployeeName(first, last) || String(name || '').trim();
    if (!display) return null;
    const split = first || last ? { firstName: first, lastName: last } : splitEmployeeName(display);
    return {
        name: display,
        firstName: split.firstName,
        lastName: split.lastName,
        phone: String(phone || '').trim(),
        email: String(email || '').trim(),
        note: String(note || '').trim(),
        isTemporary: true,
        archived: false,
        isTheatre: false,
    };
}

export function promoteTemporaryEmployee(emp) {
    const current = asEmployeeObject(emp);
    if (!current) return emp;
    return { ...current, isTemporary: false, archived: false };
}

export function archiveEmployee(emp) {
    const current = asEmployeeObject(emp);
    if (!current) return emp;
    return { ...current, archived: true };
}

export function unarchiveEmployee(emp) {
    const current = asEmployeeObject(emp);
    if (!current) return emp;
    return { ...current, archived: false };
}

export function namesMatch(a, b) {
    return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
}

export function findEmployeeIndex(list, identifier) {
    const value = String(identifier || '').trim().toLowerCase();
    if (!value) return -1;
    return (list || []).findIndex((emp) => {
        const obj = asEmployeeObject(emp);
        if (!obj) return false;
        const candidates = [obj.name, obj.login, obj.id, obj.email]
            .filter(Boolean)
            .map((item) => String(item).trim().toLowerCase());
        return candidates.includes(value);
    });
}

export function employeeNameExists(list, name, { exceptName } = {}) {
    const target = String(name || '').trim().toLowerCase();
    if (!target) return false;
    const except = String(exceptName || '').trim().toLowerCase();
    return (list || []).some((emp) => {
        const current = getEmployeeName(emp).toLowerCase();
        if (!current) return false;
        if (except && current === except) return false;
        return current === target;
    });
}

export function replacePermanentEmployees(all, nextPermanents) {
    const temps = (all || []).filter(isTemporaryEmployee);
    return [...(nextPermanents || []), ...temps];
}

export function upsertTemporaryInList(list, fields = {}) {
    const incoming = createTemporaryEmployee(fields);
    if (!incoming) {
        return { employees: list || [], employee: null, error: 'Imię i nazwisko są wymagane' };
    }
    const employees = [...(list || [])];
    const index = findEmployeeIndex(employees, incoming.name);
    if (index === -1) {
        employees.push(incoming);
        return { employees, employee: incoming };
    }
    const existing = asEmployeeObject(employees[index]) || {};
    if (!isTemporaryEmployee(existing) && isPermanentEmployee(existing)) {
        return {
            employees: list || [],
            employee: null,
            error: `Osoba „${existing.name}” jest już na stałej liście pracowników`,
        };
    }
    const merged = {
        ...existing,
        ...incoming,
        isTemporary: true,
        archived: incoming.archived || existing.archived || false,
        phone: incoming.phone || existing.phone || '',
        email: incoming.email || existing.email || '',
        note: incoming.note || existing.note || '',
    };
    employees[index] = merged;
    return { employees, employee: merged };
}

export function archiveEmployeeInList(list, identifier) {
    const employees = [...(list || [])];
    const index = findEmployeeIndex(employees, identifier);
    if (index === -1) return employees;
    employees[index] = archiveEmployee(employees[index]);
    return employees;
}

export function promoteEmployeeInList(list, identifier) {
    const employees = [...(list || [])];
    const index = findEmployeeIndex(employees, identifier);
    if (index === -1) return employees;
    employees[index] = promoteTemporaryEmployee(employees[index]);
    return employees;
}

export function applySetEmployeesPreserveTemps(current, incoming) {
    const currentList = Array.isArray(current) ? current : [];
    const incomingList = (Array.isArray(incoming) ? incoming : [])
        .map(asEmployeeObject)
        .filter(Boolean);

    const currentByName = new Map();
    currentList.forEach((emp) => {
        const name = getEmployeeName(emp).toLowerCase();
        if (name) currentByName.set(name, asEmployeeObject(emp));
    });

    const incomingNames = new Set(incomingList.map((emp) => emp.name.toLowerCase()));
    const result = incomingList.map((emp) => {
        const existing = currentByName.get(emp.name.toLowerCase());
        if (existing && isTemporaryEmployee(existing)) {
            return {
                ...existing,
                ...emp,
                isTemporary: true,
                archived: existing.archived === true,
            };
        }
        return emp;
    });

    currentList.forEach((emp) => {
        if (!isTemporaryEmployee(emp)) return;
        const name = getEmployeeName(emp).toLowerCase();
        if (name && !incomingNames.has(name)) {
            result.push(asEmployeeObject(emp));
        }
    });

    return result;
}

export function collectEmployeeEventHistory(eventsByDate, employeeName) {
    const target = String(employeeName || '').trim().toLowerCase();
    if (!target) return [];
    const history = [];
    Object.entries(eventsByDate || {}).forEach(([date, events]) => {
        (Array.isArray(events) ? events : []).forEach((ev) => {
            if (!ev || ev._isFreeDay) return;
            const hit = (ev.staff || []).map(parseStaffEntry).find((entry) => (
                entry && entry.name.toLowerCase() === target
            ));
            if (hit) {
                history.push({
                    date,
                    eventName: ev.event || ev.eventName || '',
                    calendarId: ev.calendarId || '',
                    tentative: !!hit.tentative,
                });
            }
        });
    });
    history.sort((a, b) => {
        if (a.date !== b.date) return b.date.localeCompare(a.date);
        return String(a.eventName).localeCompare(String(b.eventName), 'pl', { sensitivity: 'base' });
    });
    return history;
}

export function renameEmployeeInEvents(eventsByDate, oldName, newName) {
    const from = String(oldName || '').trim();
    const to = String(newName || '').trim();
    if (!from || !to || namesMatch(from, to)) return eventsByDate || {};
    const fromLower = from.toLowerCase();
    const next = {};
    Object.entries(eventsByDate || {}).forEach(([date, events]) => {
        next[date] = (Array.isArray(events) ? events : []).map((ev) => {
            if (!ev || !Array.isArray(ev.staff)) return ev;
            let changed = false;
            const staff = ev.staff.map((entry) => {
                const parsed = parseStaffEntry(entry);
                if (!parsed || parsed.name.toLowerCase() !== fromLower) return entry;
                changed = true;
                if (typeof entry === 'string') {
                    return parsed.tentative ? `${to} (?)` : to;
                }
                return { ...entry, name: to };
            });
            return changed ? { ...ev, staff } : ev;
        });
    });
    return next;
}

export function canEmployeeLogin(emp) {
    if (!emp || typeof emp === 'string') return true;
    return !isTemporaryEmployee(emp) && !isArchivedEmployee(emp);
}

export function shouldNotifyEmployee(emp) {
    if (!emp || typeof emp === 'string') return true;
    return !isTemporaryEmployee(emp) && !isArchivedEmployee(emp);
}

export function normalizePersonName(value) {
    return String(value || '').trim().replace(/\s+/g, ' ');
}

export function isOffListStaffName(name, employees) {
    const target = normalizePersonName(name).toLowerCase();
    if (!target) return false;
    return !(employees || []).some((emp) =>
        isPermanentEmployee(emp) && getEmployeeName(emp).toLowerCase() === target
    );
}

/** Stałe nazwisko z listy, albo wpisany tekst — bez dopisywania do katalogu pracowników. */
export function staffNameForAssignment(typed, employees) {
    const normalized = normalizePersonName(typed);
    if (!normalized) return '';
    const hit = (employees || []).find((emp) =>
        isPermanentEmployee(emp) && getEmployeeName(emp).toLowerCase() === normalized.toLowerCase()
    );
    return hit ? getEmployeeName(hit) : normalized;
}
